/**
 * De bon zoals hij op het kassascherm wordt opgebouwd.
 *
 * Alles hier is puur rekenwerk zonder opslag: dezelfde functies draaien in de
 * app, in een test en straks in de demomodus. De kassa bewaart het resultaat
 * lokaal (regel 2) en zet het in de outbox.
 *
 * Statiegeld loopt hier mee als aparte post, nooit als omzet (regel 10). Een
 * product draagt een aantal bekers; die borg komt als eigen regel op de bon en
 * blijft buiten de BTW. Bekers die de klant inlevert gaan er in dezelfde
 * handeling weer af.
 */

import { btwOverzicht, type BtwOverzicht, type BtwTarief } from './btw.js';
import { sommeer, vermenigvuldig, type Cent } from './geld.js';

export interface Product {
  menuItemId: string;
  omschrijving: string;
  stukprijsCent: Cent;
  btwTarief: BtwTarief;
  bekers?: number;
}

export interface Bonregel extends Product {
  /** Eigen id van de regel; twee keer hetzelfde product kan als aparte regel. */
  regelId: string;
  aantal: number;
}

export type BonStatus = 'open' | 'afgerond';

export interface Bon {
  /**
   * De borg per beker zoals die gold toen de bon werd aangeslagen. Net als de
   * prijs hoort dit bij de bon zelf: een latere wijziging van het evenement
   * verandert een oude bon niet.
   */
  bekerborgCent: Cent;
  /** Bekers die de klant bij deze bestelling inlevert; borg gaat eraf. */
  ingeleverdeBekers: number;
  /** Door het apparaat zelf gemaakt (regel 3). De sleutel voor idempotente sync. */
  clientUuid: string;
  kraamdeelnameId: string;
  /** Pas bij afronden toegekend, 1 t/m 99 per kraam per evenementdag. */
  nummer: number | null;
  evenementdag: string;
  regels: Bonregel[];
  status: BonStatus;
  aangeslagenOp: string;
  afgerondOp: string | null;
}

export interface BonTotalen {
  aantalArtikelen: number;
  /** Alleen de producten, inclusief BTW. Dit is de omzet. */
  productenCent: Cent;
  /** Bekers die met deze bestelling meegaan. */
  bekersUit: number;
  statiegeldUitCent: Cent;
  /** Bekers die de klant inlevert. */
  bekersIn: number;
  statiegeldInCent: Cent;
  /** Uit min in. Positief betekent dat de kraam borg heeft ontvangen. */
  statiegeldSaldoCent: Cent;
  /** Wat de klant betaalt: producten plus het statiegeldsaldo. */
  totaalCent: Cent;
  /** Alleen over de producten; statiegeld valt buiten de BTW. */
  btw: BtwOverzicht;
}

export function maakLegeBon(invoer: {
  clientUuid: string;
  kraamdeelnameId: string;
  evenementdag: string;
  aangeslagenOp: string;
  bekerborgCent?: Cent;
}): Bon {
  return {
    clientUuid: invoer.clientUuid,
    kraamdeelnameId: invoer.kraamdeelnameId,
    bekerborgCent: invoer.bekerborgCent ?? 0,
    ingeleverdeBekers: 0,
    nummer: null,
    evenementdag: invoer.evenementdag,
    regels: [],
    status: 'open',
    aangeslagenOp: invoer.aangeslagenOp,
    afgerondOp: null,
  };
}

function eisOpen(bon: Bon): void {
  if (bon.status !== 'open') {
    // Regel 4: een afgeronde bon verandert niet meer. Wat er nog moet gebeuren
    // gaat via een correctie met reden.
    throw new Error('Deze bon is al afgerond en kan niet meer worden aangepast.');
  }
}

/**
 * Tikt een product aan. Staat het er al op, dan gaat het aantal omhoog — dat
 * scheelt de verkoper een handeling bij drie dezelfde biertjes.
 */
export function voegProductToe(bon: Bon, product: Product, regelId: string, aantal = 1): Bon {
  eisOpen(bon);
  if (!Number.isSafeInteger(aantal) || aantal < 1) {
    throw new Error(`aantal moet minstens 1 zijn, kreeg ${String(aantal)}`);
  }

  const bestaande = bon.regels.find((regel) => regel.menuItemId === product.menuItemId);
  if (bestaande !== undefined) {
    return wijzigAantal(bon, bestaande.regelId, bestaande.aantal + aantal);
  }

  return {
    ...bon,
    regels: [...bon.regels, { ...product, regelId, aantal }],
  };
}

/** Zet het aantal. Op nul verdwijnt de regel; dat scheelt een apart kruisje. */
export function wijzigAantal(bon: Bon, regelId: string, aantal: number): Bon {
  eisOpen(bon);
  if (!Number.isSafeInteger(aantal) || aantal < 0) {
    throw new Error(`aantal moet 0 of meer zijn, kreeg ${String(aantal)}`);
  }
  if (aantal === 0) return verwijderRegel(bon, regelId);

  return {
    ...bon,
    regels: bon.regels.map((regel) => (regel.regelId === regelId ? { ...regel, aantal } : regel)),
  };
}

export function verwijderRegel(bon: Bon, regelId: string): Bon {
  eisOpen(bon);
  return { ...bon, regels: bon.regels.filter((regel) => regel.regelId !== regelId) };
}

/**
 * Rekent de bon door. De borg komt standaard van de bon zelf; los meegeven kan
 * ook, bijvoorbeeld om te laten zien wat een bon met een ander tarief zou doen.
 */
export function bontotalen(bon: Bon, bekerborgCent: Cent = bon.bekerborgCent ?? 0): BonTotalen {
  const regeltotalen = bon.regels.map((regel) => ({
    brutoCent: vermenigvuldig(regel.stukprijsCent, regel.aantal),
    tarief: regel.btwTarief,
  }));

  const btw = btwOverzicht(regeltotalen);
  const productenCent = sommeer(regeltotalen.map((regel) => regel.brutoCent));

  const bekersUit = bon.regels.reduce((som, regel) => som + (regel.bekers ?? 0) * regel.aantal, 0);
  // Bonnen die met een oudere versie van de app zijn opgeslagen kennen dit veld
  // nog niet. Dat mag de kassa nooit laten omvallen: na een app-update staan er
  // gewoon bonnen van gisteren op het toestel.
  const bekersIn = bon.ingeleverdeBekers ?? 0;
  const statiegeldUitCent = vermenigvuldig(bekerborgCent, bekersUit);
  const statiegeldInCent = vermenigvuldig(bekerborgCent, bekersIn);
  const statiegeldSaldoCent = statiegeldUitCent - statiegeldInCent;

  return {
    aantalArtikelen: bon.regels.reduce((som, regel) => som + regel.aantal, 0),
    productenCent,
    bekersUit,
    statiegeldUitCent,
    bekersIn,
    statiegeldInCent,
    statiegeldSaldoCent,
    totaalCent: productenCent + statiegeldSaldoCent,
    btw,
  };
}

/**
 * Zet het aantal ingeleverde bekers. Verrekenen met de lopende bestelling is
 * één handeling: de borg gaat er meteen af.
 */
export function zetIngeleverdeBekers(bon: Bon, aantal: number): Bon {
  eisOpen(bon);
  if (!Number.isSafeInteger(aantal) || aantal < 0) {
    throw new Error(`aantal bekers moet 0 of meer zijn, kreeg ${String(aantal)}`);
  }
  return { ...bon, ingeleverdeBekers: aantal };
}

/**
 * Rondt de bon af en geeft hem zijn nummer. Een lege bon afronden kan niet:
 * dat is altijd een misklik.
 */
export function rondBonAf(bon: Bon, nummer: number, afgerondOp: string): Bon {
  eisOpen(bon);
  if (bon.regels.length === 0 && (bon.ingeleverdeBekers ?? 0) === 0) {
    throw new Error('Een lege bon kan niet worden afgerond.');
  }
  return { ...bon, status: 'afgerond', nummer, afgerondOp };
}
